import React, { forwardRef } from "react";
import clsx from "clsx";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { getDateWithDayOfWeekString } from "../../../libs/dateFormat";

const CustomInput = ({ value, onClick }: any) => (
  <div
    onClick={onClick}
    className="flex items-center border rounded px-2 py-2 text-gray-700 gap-2 h-bottom-button"
  >
    <CalendarIcon />
    <input
      type="text"
      value={value}
      readOnly
      className="border-0 focus:outline-none w-full"
      placeholder="선택"
    />
  </div>
);

const DateReservationInput = ({ value, onClick }: any) => (
  <div
    onClick={onClick}
    className="flex border border-gray-300 rounded px-4 py-2 h-bottom-button gap-2"
  >
    <div className="relative focus:outline-none w-full flex items-center">
      <CalendarIcon />
      &nbsp;
      <input
        type="text"
        value={getDateWithDayOfWeekString(new Date(value))}
        readOnly
        className="border-0 focus:outline-none w-full"
        placeholder="선택"
      />
    </div>
  </div>
);

const ReservationInput = forwardRef<HTMLDivElement>(
  ({ value, onClick }: any, ref) => (
    <div
      onClick={onClick}
      className="flex border border-gray-300 px-3 py-2 h-bottom-button gap-2"
      ref={ref}
    >
      <div className="relative focus:outline-none w-full flex items-center">
        {getDateWithDayOfWeekString(new Date(value))}
        <div
          className={clsx([
            "pointer-events-none absolute inset-y-0 right-0 flex items-center bg-white text-gray-700 transition-all",
          ])}
        >
          <svg
            width="15"
            height="15"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g opacity="0.5">
              <path
                d="M6 9L12 15L18 9"
                stroke="black"
                strokeWidth="1.75"
                strokeLinecap="square"
                strokeLinejoin="round"
              />
            </g>
          </svg>
        </div>
      </div>
    </div>
  )
);

export type DateRangeInputType =
  | "customInput"
  | "reservation"
  | "date"
  | undefined;

const CustomDatePicker = ({
  className,
  wrapperClassName,
  date,
  inputType,
  minDate,
  maxDate,
  dateFormat,
  popperPlacement,
  onChange,
  startDate,
  endDate,
}: {
  className?: string;
  wrapperClassName?: string;
  date: Date | null;
  inputType?: DateRangeInputType;
  minDate?: Date;
  maxDate?: Date;
  dateFormat?: string;
  popperPlacement?:
    | "top-start"
    | "top-end"
    | "bottom-start"
    | "bottom-end"
    | "right-start"
    | "right-end"
    | "left-start"
    | "left-end";
  onChange: (date: Date | null) => void;
  startDate?: Date;
  endDate?: Date;
}) => {
  let customInput;

  if (inputType === "reservation") customInput = <ReservationInput />;
  else if (inputType === "date") customInput = <DateReservationInput />;
  else customInput = <CustomInput />;

  return (
    <div className="custom-date-picker w-full">
      <DatePicker
        dateFormat={dateFormat}
        selected={date}
        onChange={(date: Date | null) => onChange?.(date)}
        customInput={customInput}
        minDate={minDate}
        maxDate={maxDate}
        className={className}
        wrapperClassName={wrapperClassName}
        popperPlacement={popperPlacement}
        startDate={startDate}
        endDate={endDate}
      />
    </div>
  );
};

export const CalendarIcon = () => (
  <svg
    width="25"
    height="24"
    viewBox="0 0 25 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M4.09326 8.92246H20.5805"
      stroke="black"
      strokeWidth="1.5"
      strokeLinecap="square"
    />
    <path
      d="M16.4413 12.535H16.4498"
      stroke="black"
      strokeWidth="1.5"
      strokeLinecap="square"
    />
    <path
      d="M12.3373 12.535H12.3458"
      stroke="black"
      strokeWidth="1.5"
      strokeLinecap="square"
    />
    <path
      d="M8.22398 12.535H8.23255"
      stroke="black"
      strokeWidth="1.5"
      strokeLinecap="square"
    />
    <path
      d="M16.4413 16.13H16.4498"
      stroke="black"
      strokeWidth="1.5"
      strokeLinecap="square"
    />
    <path
      d="M12.3373 16.13H12.3458"
      stroke="black"
      strokeWidth="1.5"
      strokeLinecap="square"
    />
    <path
      d="M8.22398 16.13H8.23255"
      stroke="black"
      strokeWidth="1.5"
      strokeLinecap="square"
    />
    <path
      d="M16.073 2.07349V5.11747"
      stroke="black"
      strokeWidth="1.5"
      strokeLinecap="square"
    />
    <path
      d="M8.60131 2.07349V5.11747"
      stroke="black"
      strokeWidth="1.5"
      strokeLinecap="square"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M20.6583 3.53418H4.0083V20.5735H20.6583V3.53418Z"
      stroke="black"
      strokeWidth="1.5"
      strokeLinecap="square"
    />
  </svg>
);

export const ReservationCalIcon = () => (
  <svg
    width="36"
    height="36"
    viewBox="0 0 36 36"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M5.65222 14.1832H30.3584"
      stroke="black"
      strokeWidth="1.5"
      strokeLinecap="square"
    />
    <path
      d="M24.1558 19.5965H24.1686"
      stroke="black"
      strokeWidth="1.5"
      strokeLinecap="square"
    />
    <path
      d="M18.0056 19.5965H18.0184"
      stroke="black"
      strokeWidth="1.5"
      strokeLinecap="square"
    />
    <path
      d="M11.8417 19.5965H11.8546"
      stroke="black"
      strokeWidth="1.5"
      strokeLinecap="square"
    />
    <path
      d="M24.1558 24.9837H24.1686"
      stroke="black"
      strokeWidth="1.5"
      strokeLinecap="square"
    />
    <path
      d="M18.0056 24.9837H18.0184"
      stroke="black"
      strokeWidth="1.5"
      strokeLinecap="square"
    />
    <path
      d="M11.8417 24.9837H11.8546"
      stroke="black"
      strokeWidth="1.5"
      strokeLinecap="square"
    />
    <path
      d="M23.6043 3.91992V8.48135"
      stroke="black"
      strokeWidth="1.5"
      strokeLinecap="square"
    />
    <path
      d="M12.4072 3.91992V8.48135"
      stroke="black"
      strokeWidth="1.5"
      strokeLinecap="square"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M30.4751 6.10889H5.5249V31.6424H30.4751V6.10889Z"
      stroke="black"
      strokeWidth="1.5"
      strokeLinecap="square"
    />
  </svg>
);

export default CustomDatePicker;
